import React from 'react';
import { Card } from '../ui/card';
import { useState } from 'react';
import { Pin, Heart, Calendar, MessageSquare, MoreVertical, Edit, Trash2, PinOff, ChevronRight } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { useTranslation } from 'react-i18next';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "../ui/dropdown-menu";
import { formatDate, formatCommentDate, getCategoryColor, getInitials } from '../../lib/utils';

const PostCard = ({ post, onLike, onComment, onOpenComments, onEdit, onDelete, onTogglePin, currentUserId, isBuildingManager }) => {
  const { t } = useTranslation();
  const reactions = post.reactions || { comments: [], likes: 0, isLiked: false };
  post = { ...post, reactions };

  const [dropdownOpen, setDropdownOpen] = useState(false);

  const isAuthor = post.author_id === currentUserId;
  const canEdit = isAuthor;
  const canPin = isBuildingManager;
  const canManage = canEdit || canPin;
  
  const latestComment = post.reactions.comments.length > 0 
    ? post.reactions.comments[post.reactions.comments.length - 1] 
    : null;

  const handleDelete = (postId) => {
    setDropdownOpen(false);
    onDelete(postId);
  };

  const handleEdit = (post) => {
    setDropdownOpen(false);
    onEdit(post);
  };
  

  return (
    <Card className={`p-4 md:p-6 hover:shadow-lg transition-all duration-200 bg-white dark:bg-neutral-800 ${
      post.is_pinned ? 'border-dashed border-2' : 'border border-neutral-200 dark:border-neutral-700'
    }`}>
      <div className="space-y-3 md:space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex flex-col gap-1.5 md:gap-2">
            {post.is_pinned ? (
              <div className="flex items-center gap-1 text-xs md:text-sm text-primary">
                <Pin className="w-3 h-3 md:w-4 md:h-4" />
                <span>{t('advalvas.pinned')}</span>
              </div>
            ) : (
              <div className="flex items-center gap-1 text-xs md:text-sm text-muted-foreground">
                <span></span>
              </div>
            )}
            <span className={`text-[10px] md:text-xs px-1.5 md:px-2 py-0.5 md:py-1 rounded-md w-fit uppercase font-medium tracking-wider ${getCategoryColor(post.category)}`}>
              {t(`postTypes.${post.category}`)}
            </span>       
            <div>
              <h3 className="text-base md:text-lg font-semibold line-clamp-2">{post.title}</h3>
              <div className="flex items-center gap-2">
                <p className="text-xs md:text-sm text-muted-foreground">{post.author_name}</p>
              </div>
            </div>
          </div>

          {canManage && (
            <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
              <DropdownMenuTrigger asChild>
                <button className="rounded-full p-1 md:p-1.5 hover:bg-neutral-100 dark:hover:bg-neutral-800 flex-shrink-0">
                  <MoreVertical className="h-4 w-4 text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {canEdit && (
                  <DropdownMenuItem onClick={(e) => {
                    e.stopPropagation();
                    handleEdit(post);
                  }}>
                    <Edit className="h-4 w-4 mr-2" />
                    {t('postActions.edit')}
                  </DropdownMenuItem>
                )}
                {canEdit && (
                  <DropdownMenuItem 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(post.id);
                    }}
                    className="text-red-500"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    {t('postActions.delete')}
                  </DropdownMenuItem>
                )}
                {canPin && (
                  <DropdownMenuItem onClick={() => onTogglePin(post.id, !post.is_pinned)}>
                    {post.is_pinned ? (
                      <>
                        <PinOff className="h-4 w-4 mr-2" />
                        {t('postActions.unpin')}
                      </>
                    ) : (
                      <>
                        <Pin className="h-4 w-4 mr-2" />
                        {t('postActions.pin')}
                      </>
                    )}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {/* Content */}
        <p className="text-xs md:text-sm text-muted-foreground line-clamp-3">{post.description}</p>

        {/* Footer with Date and Interaction Buttons */}
        <div>
          {/* Mobile: Instagram-style layout (date first, then interaction buttons) */}
          <div className="md:hidden space-y-2">
            {/* Date information */}
            <div className="flex items-center text-xs text-muted-foreground">
              <Calendar className="w-3 h-3 mr-1 flex-shrink-0" />
              <span>{formatDate(post.created_at)}</span>
            </div>
            
            {/* Instagram-style interaction buttons */}
            <div className="flex items-center gap-1">
              <button 
                className={`flex items-center gap-1.5 py-1.5 text-xs ${
                  post.reactions.isLiked ? 'text-red-500' : 'text-neutral-700 dark:text-neutral-200'
                }`}
                onClick={() => onLike(post.id)}
              >
                <Heart 
                  className={`w-4 h-4 transition-transform ${
                    post.reactions.isLiked ? 'fill-red-500 text-red-500' : ''
                  }`}
                />
                <span className="font-medium">{post.reactions.likes}</span>
              </button>
              
              <button 
                className="flex items-center gap-1.5 py-1.5 text-xs text-neutral-700 dark:text-neutral-200"
                onClick={() => onOpenComments(post)}
              >
                <MessageSquare className="w-4 h-4" />
                <span className="font-medium">{post.reactions.comments.length}</span>
              </button>
            </div>
          </div>

          {/* Desktop: Date and action buttons in one row */}
          <div className="hidden md:flex items-center justify-between mt-3">
            <div className="flex items-center text-sm text-muted-foreground">
              <Calendar className="w-4 h-4 mr-2 flex-shrink-0" />
              <span>{formatDate(post.created_at)}</span>
            </div>
            <div className="flex items-center gap-2">
              <button 
                className="flex items-center gap-1.5 py-1.5 px-3 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors text-sm"
                onClick={() => onOpenComments(post)}
              >
                <MessageSquare className="w-4 h-4" />
                <span>{t('advalvas.commentsCount', { count: post.reactions.comments.length })}</span>
              </button>
              <button 
                className={`flex items-center gap-1.5 py-1.5 px-3 rounded-md transition-colors text-sm ${
                  post.reactions.isLiked 
                  ? 'text-red-500' 
                  : 'hover:bg-neutral-100 dark:hover:bg-neutral-800'
                }`}
                onClick={() => onLike(post.id)}
              >
                <Heart 
                  className={`w-4 h-4 transition-transform ${
                    post.reactions.isLiked ? 'fill-red-500 text-red-500' : ''
                  }`}
                />
<span>
  {post.reactions.likes === 1 
    ? t('advalvas.likes', { count: 1 })
    : t('advalvas.likes_plural', { count: post.reactions.likes })}
</span>              </button>
            </div>
          </div>
        </div>
        
        {/* Latest comment section */}
        {latestComment && (
          <div className="mt-2 pt-2 md:mt-3 md:pt-3 border-t border-neutral-200 dark:border-neutral-700 group">
            {/* Comment header with avatar and metadata */}
            <div className="flex items-start gap-1.5 md:gap-2 mb-1 md:mb-1.5">
              <Avatar className="h-5 w-5 md:h-6 md:w-6">
                <AvatarImage src={latestComment.user_avatar} />
                <AvatarFallback className="text-[10px] md:text-xs">
                  {getInitials(latestComment.author)}
                </AvatarFallback>
              </Avatar>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-2">
                  <p className="text-[10px] md:text-xs font-medium truncate">
                    {latestComment.author}
                  </p>
                  <p className="text-[10px] md:text-xs text-muted-foreground shrink-0">
                    {formatCommentDate(latestComment.created_at)}
                  </p>
                </div>
                
                {/* Comment text with hover effect */}
                <p className="text-[10px] md:text-xs text-muted-foreground line-clamp-2 group-hover:text-neutral-900 dark:group-hover:text-neutral-100 transition-colors">
                  {latestComment.text}
                </p>
              </div>
            </div>

            {/* "View all" button (only shows if multiple comments) */}
            {post.reactions.comments.length > 1 && (
              <button
                className="w-full mt-1 text-[10px] md:text-xs text-primary flex items-center justify-end gap-1 hover:underline"
                onClick={() => onOpenComments(post)}
              >
                {t('advalvas.viewAllComments', { count: post.reactions.comments.length })}
                <ChevronRight className="h-2 w-2 md:h-3 md:w-3" />
              </button>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};

export default PostCard;